import { Point, PointData } from "pixi.js";

export class Geom {
	static equal(a: PointData, b: PointData) {
		return a.x === b.x && a.y === b.y;
	}

	public static distance(from: PointData, to: PointData): number {
		return Math.sqrt((from.x - to.x) ** 2 + (from.y - to.y) ** 2);
	}


	public static dotProduct(a: PointData, b: PointData): number {
		return a.x * b.x + a.y * b.y;
	}

	public static project(vector: PointData, targetVector: PointData): PointData {
		return Geom.multiply(targetVector, Geom.dotProduct(vector, targetVector) / Geom.dotProduct(targetVector, targetVector))
	}

	public static angle(from: PointData, to: PointData): number {
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		return Math.atan2(dy, dx);
	}

	public static moveTowards(from: PointData, to: PointData, amount: number) {
		const angle = this.angle(from, to);
		const vx = Math.cos(angle);
		const vy = Math.sin(angle);
		from.x += vx * amount;
		from.y += vy * amount;
	}

	public static moveInDirection(from: PointData, angle: number, amount: number) {
		const vx = Math.cos(angle);
		const vy = Math.sin(angle);
		from.x += vx * amount;
		from.y += vy * amount;
	}

	public static normalize(from: PointData): PointData {
		return Geom.divide(from, Geom.distance({ x: 0, y: 0 }, from));
	}

	public static divide(from: PointData, scalar: number): PointData {
		return {
			x: from.x / scalar,
			y: from.y / scalar,
		}
	}

	public static multiply(from: PointData, scalar: number): PointData {
		return {
			x: from.x * scalar,
			y: from.y * scalar,
		}
	}

	public static lerp(x: number, min: number, max: number) {
		return (x - min) / (max - min);
	}

	public static inverseLerp(x: number, min: number, max: number) {
		return min + x * (max - min);
	}

	public static remap(fromMin: number, fromMax: number, x: number, toMin: number, toMax: number): number {
		return this.inverseLerp(this.lerp(x, fromMin, fromMax), toMin, toMax);
	}

	public static distanceFromPointToLine(
		x0: number, y0: number, // point
		x1: number, y1: number, // line point 1
		x2: number, y2: number, // line point 2
	): number {
		return Math.abs(
			(y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1
		) / Math.sqrt(
			Math.pow(y2 - y1, 2) + Math.pow(x2 - x1, 2)
		);
	}

	public static subtract(a: PointData, b: PointData): PointData {
		return {
			x: a.x - b.x,
			y: a.y - b.y,
		}
	}
}
